import { useEffect, useRef, useState, type ReactNode } from "react";

import type { MailFolder } from "../lib/types.js";

/**
 * Eén dialoog voor "Nieuwe map" en "Naam wijzigen" — het verschil is alleen de
 * titel, de beginwaarde en welke aanroep eronder hangt.
 *
 * De naam wordt hier niet gekeurd: dat doet de server, die als enige de
 * delimiter en de bestaande mappen kent. De melding die terugkomt is
 * Nederlands en toonbaar, dus die zetten we letterlijk onder het veld.
 */

export interface FolderDialogState {
  mode: "create" | "rename";
  /** Bij "create" de bovenliggende map (null = hoogste niveau). */
  parent: MailFolder | null;
  /** Bij "rename" de map zelf. */
  target: MailFolder | null;
}

interface FolderDialogProps {
  state: FolderDialogState;
  busy: boolean;
  error: string | null;
  onSubmit: (name: string) => void;
  onClose: () => void;
}

export function FolderDialog({
  state,
  busy,
  error,
  onSubmit,
  onClose,
}: FolderDialogProps): ReactNode {
  const [name, setName] = useState(state.mode === "rename" ? (state.target?.name ?? "") : "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const opener = document.activeElement;
    inputRef.current?.focus();
    inputRef.current?.select();
    return () => {
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const titel = state.mode === "create" ? "Nieuwe map" : "Naam wijzigen";
  const onder = state.mode === "create" && state.parent !== null ? state.parent.name : null;

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true" aria-label={titel}>
      <button type="button" className="sheet-dismiss" aria-label="Sluiten" onClick={onClose} />

      <form
        className="sheet sheet--dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) onSubmit(name);
        }}
      >
        <div className="sheet-head">
          <h2 className="sheet-title">{titel}</h2>
        </div>

        <div className="sheet-body">
          <label className="field">
            <span className="field-label">Naam</span>
            <input
              ref={inputRef}
              className="field-input"
              value={name}
              maxLength={100}
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          {onder !== null && <p className="field-hint">Komt onder {onder} te staan.</p>}
          {error !== null && <p className="composer-error" role="alert">{error}</p>}
        </div>

        <div className="sheet-foot sheet-foot--actions">
          <button type="button" className="button button--ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={busy || name.trim() === ""}
          >
            {state.mode === "create" ? "Map maken" : "Opslaan"}
          </button>
        </div>
      </form>
    </div>
  );
}
